import type { PlanoSeleccion } from './domain.js';
import type { AvanceObraConcepto, AvanceObraPeriodo } from './avanceObra.js';

/**
 * Avance de obra (OPUS ci, idea 11) — la lógica PURA del «video» de la obra.
 *
 * Entra lo que reportó m1 (o m2): para cada periodo, el acumulado de cada concepto. Sale, para una FECHA, cuánto
 * está hecho de cada pieza del modelo. Sin geometría y sin red: el visor le pasa las piezas YA ORDENADAS (la
 * geometría vive en la web) y pinta lo que esto devuelve.
 *
 *  - Línea de tiempo: dentro de un periodo el acumulado crece de lo que traía al cierre del periodo anterior a lo
 *    que trae al cerrar el suyo, en línea recta (m1 no sabe QUÉ DÍA se hizo cada cosa, solo cuánto en el periodo).
 *    Entre periodos con hueco el avance se queda quieto; antes del primero es 0; después del último, el último.
 *  - El avance es porcentaje del CONTRATADO de m1 (acumulado / contratado), no m² del modelo: el modelo y m1 miden
 *    distinto, y el avance es el de m1. Un porcentaje mayor a 100 % se reporta real, pero se PINTA topado en 100 %.
 *  - Con el concepto al X %, sus piezas se completan en orden y la última queda a medias (mismo criterio del demo).
 *  - Una pieza en varios conceptos principales toma el MÍNIMO de sus avances: no se da por hecha hasta que lo esté
 *    en todos.
 */

const MS_POR_DIA = 86_400_000;
const EPS = 1e-9;

/** `YYYY-MM-DD` → días desde 1970 (UTC). Sin horas: los periodos de las estimaciones son fechas. */
export function diaDeFecha(iso: string): number {
  return Math.floor(Date.parse(`${iso}T00:00:00Z`) / MS_POR_DIA);
}

export interface Nudo {
  /** Día (ver `diaDeFecha`). */
  t: number;
  /** Valor acumulado en ese día. */
  v: number;
}

/**
 * Los nudos de una serie acumulada. `acumuladoPorNumero` trae el acumulado del concepto SOLO en los periodos en
 * que aparece; en los demás se arrastra el anterior (el concepto no avanzó ese periodo). Los periodos se
 * recorren por `numero`; si uno empieza antes de donde terminó el anterior (traslape) arranca donde terminó
 * aquel, para que el tiempo nunca retroceda.
 */
export function nudosDeAvance(
  periodos: readonly Pick<AvanceObraPeriodo, 'numero' | 'fechaInicial' | 'fechaFinal'>[],
  acumuladoPorNumero: ReadonlyMap<number, number>,
): Nudo[] {
  const ordenados = [...periodos].sort((a, b) => a.numero - b.numero);
  const nudos: Nudo[] = [];
  let acum = 0;
  for (const p of ordenados) {
    const ini = diaDeFecha(p.fechaInicial);
    const fin = Math.max(diaDeFecha(p.fechaFinal), ini);
    const ultimoT = nudos.length > 0 ? nudos[nudos.length - 1]!.t : -Infinity;
    const arranque = Math.max(ini, ultimoT);
    if (nudos.length === 0 || arranque > ultimoT) nudos.push({ t: arranque, v: acum });
    acum = acumuladoPorNumero.get(p.numero) ?? acum;
    nudos.push({ t: Math.max(fin, arranque), v: acum });
  }
  return nudos;
}

/** El valor en el día `t`: recta entre nudos, 0 antes del primero y el último después. Un salto en el mismo día cuenta al llegar. */
export function interpolar(nudos: readonly Nudo[], t: number): number {
  if (nudos.length === 0) return 0;
  const primero = nudos[0]!;
  if (t < primero.t) return 0;
  for (let i = 1; i < nudos.length; i++) {
    const a = nudos[i - 1]!;
    const b = nudos[i]!;
    if (t <= b.t) {
      if (b.t === a.t) return b.v;
      return a.v + ((t - a.t) / (b.t - a.t)) * (b.v - a.v);
    }
  }
  return nudos[nudos.length - 1]!.v;
}

/** El avance (fracción del contratado, SIN topar) de un concepto en el día `t`. */
export function pctDeConceptoEn(c: Pick<AvanceObraConcepto, 'porPeriodo'>, periodos: readonly AvanceObraPeriodo[], t: number): number {
  const acum = new Map(c.porPeriodo.map((p) => [p.numero, p.pct] as const));
  return interpolar(nudosDeAvance(periodos, acum), t);
}

/** El importe acumulado de toda la obra en el día `t` (la curva S). */
export function importeAcumuladoEn(periodos: readonly AvanceObraPeriodo[], t: number): number {
  const acum = new Map(periodos.map((p) => [p.numero, p.importeAcumulado] as const));
  return interpolar(nudosDeAvance(periodos, acum), t);
}

/** El primer y el último día con avance posible (para el control de reproducción). null si no hay periodos. */
export function rangoDeFechas(periodos: readonly Pick<AvanceObraPeriodo, 'fechaInicial' | 'fechaFinal'>[]): { desde: number; hasta: number } | null {
  if (periodos.length === 0) return null;
  let desde = Infinity;
  let hasta = -Infinity;
  for (const p of periodos) {
    desde = Math.min(desde, diaDeFecha(p.fechaInicial));
    hasta = Math.max(hasta, diaDeFecha(p.fechaFinal));
  }
  return { desde, hasta };
}

// ----------------------------------------------------------------------- piezas

export interface PiezaDeConcepto {
  /** IFC: expressID de la pieza, como texto (la misma clave de `instColores` del visor). */
  id: string;
  /** Cuánto pesa la pieza en el avance del concepto (su cantidad). */
  peso: number;
}

interface LineaConPieza {
  pieza?: string;
  parcial: number;
  clase?: string;
}

/**
 * Las piezas de un concepto: los ids de su selección (y de los «además» de un renglón por concepto), con su peso.
 * El peso sale de la foto del generador (lo que cada pieza aporta a la cantidad) si trae TODAS; si falta alguna,
 * todas pesan igual — nunca se mezclan pesos reales con inventados. Las líneas de descuento no suman.
 */
export function piezasDeConcepto(
  seleccion: PlanoSeleccion | null,
  generador?: { lineas: readonly LineaConPieza[] } | null,
): PiezaDeConcepto[] {
  if (!seleccion) return [];
  const ids: string[] = [];
  const vistos = new Set<string>();
  for (const s of [seleccion, ...(seleccion.ademas ?? [])]) {
    for (const id of s.ids ?? []) {
      if (!vistos.has(id)) {
        vistos.add(id);
        ids.push(id);
      }
    }
  }
  if (ids.length === 0) return [];
  const pesoDe = new Map<string, number>();
  for (const l of generador?.lineas ?? []) {
    if (l.pieza === undefined || l.clase === 'descuento') continue;
    pesoDe.set(l.pieza, (pesoDe.get(l.pieza) ?? 0) + l.parcial);
  }
  const todasConPeso = ids.every((id) => (pesoDe.get(id) ?? 0) > 0);
  return ids.map((id) => ({ id, peso: todasConPeso ? pesoDe.get(id)! : 1 }));
}

/**
 * Con el concepto al `pct` (0 a 1, ya topado), cuánto de cada pieza está hecho (0 a 1). Las piezas vienen en el
 * orden en que se construyen: las primeras se completan, la siguiente queda a medias y el resto a 0.
 */
export function repartirPiezas(pct: number, piezas: readonly PiezaDeConcepto[]): Map<string, number> {
  const out = new Map<string, number>();
  if (piezas.length === 0) return out;
  const tope = Math.max(0, Math.min(1, pct));
  const total = piezas.reduce((s, p) => s + (p.peso > 0 ? p.peso : 0), 0);
  // Sin pesos útiles, todas pesan igual.
  const pesos = total > 0 ? piezas.map((p) => (p.peso > 0 ? p.peso : 0)) : piezas.map(() => 1);
  const suma = total > 0 ? total : piezas.length;
  let resta = tope * suma;
  piezas.forEach((p, i) => {
    const w = pesos[i]!;
    const f = w > 0 ? Math.max(0, Math.min(1, resta / w)) : resta > EPS ? 1 : 0;
    resta -= w;
    out.set(p.id, f);
  });
  return out;
}

export type EstadoDePieza = 'terminada' | 'en_proceso' | 'pendiente';

export function estadoDePieza(f: number): EstadoDePieza {
  if (f >= 1 - EPS) return 'terminada';
  if (f > EPS) return 'en_proceso';
  return 'pendiente';
}

export interface ConceptoParaPintar {
  /** El avance del concepto: nudos de su serie (fracción del contratado). */
  nudos: readonly Nudo[];
  /** Sus piezas, YA en el orden de construcción. */
  piezas: readonly PiezaDeConcepto[];
}

/**
 * Lo hecho de cada pieza del modelo en el día `t`. Una pieza que está en varios conceptos toma el mínimo.
 * `f` 0 a 1; el estado sale de él.
 */
export function avanceDePiezasEn(conceptos: readonly ConceptoParaPintar[], t: number): Map<string, { f: number; estado: EstadoDePieza }> {
  const f = new Map<string, number>();
  for (const c of conceptos) {
    const pct = Math.max(0, Math.min(1, interpolar(c.nudos, t)));
    for (const [id, x] of repartirPiezas(pct, c.piezas)) f.set(id, Math.min(f.get(id) ?? 1, x));
  }
  return new Map([...f].map(([id, x]) => [id, { f: x, estado: estadoDePieza(x) }] as const));
}

/**
 * Arma los `ConceptoParaPintar` de la lectura del servidor (solo conceptos principales atados y con piezas). Las
 * piezas y sus pesos vienen ya en `c.piezas` (el servidor las saca del generador); `generadores` es solo un respaldo
 * para quien arme la lectura a mano.
 */
export function conceptosParaPintar(
  periodos: readonly AvanceObraPeriodo[],
  conceptos: readonly AvanceObraConcepto[],
  ordenarPiezas: (piezas: PiezaDeConcepto[]) => PiezaDeConcepto[] = (p) => p,
  generadores: ReadonlyMap<string, { lineas: readonly LineaConPieza[] } | null> = new Map(),
): ConceptoParaPintar[] {
  const out: ConceptoParaPintar[] = [];
  for (const c of conceptos) {
    if (c.conceptoId === null || c.rol === 'complemento') continue;
    const base = c.piezas.length > 0 ? [...c.piezas] : piezasDeConcepto(c.seleccion, generadores.get(c.conceptoId) ?? null);
    const piezas = ordenarPiezas(base);
    if (piezas.length === 0) continue;
    const acum = new Map(c.porPeriodo.map((p) => [p.numero, p.pct] as const));
    out.push({ nudos: nudosDeAvance(periodos, acum), piezas });
  }
  return out;
}
